<script setup lang="ts">
import { computed } from "vue";

/**
 * Badge — read-only status indicator, counter, and metadata pill.
 *
 * Distinction from TagItem:
 *   - Badge  = read-only; conveys status, counts, or metadata (no delete action).
 *   - TagItem = editable label; includes a delete/close button and emits `onDelete`.
 *
 * A11y note: when this badge is placed beside a button or link as a count indicator,
 * the parent interactive element must also convey the meaning via `aria-label`.
 * Example: `<button aria-label="Notifications, 3 unread"><Badge>3</Badge></button>`
 */

const props = withDefaults(
  defineProps<{
    /** Semantic colour variant. Maps to brand colour families: -50 bg + -600 text. */
    variant?: "neutral" | "info" | "success" | "warning" | "danger";
    /** Visual size of the badge. */
    size?: "sm" | "md";
    /**
     * Renders as a standalone status dot (coloured circle, no text).
     * When true the default slot is ignored.
     * The dot is aria-hidden — the parent must supply accessible context.
     */
    dot?: boolean;
  }>(),
  {
    variant: "neutral",
    size: "md",
    dot: false,
  }
);

const colorClass = computed<string>(() => {
  switch (props.variant) {
    case "info":
      return "bg-app-blue-50 text-app-blue-600";
    case "success":
      return "bg-app-green-50 text-app-green-600";
    case "warning":
      return "bg-app-yellow-50 text-app-yellow-600";
    case "danger":
      return "bg-app-red-50 text-app-red-600";
    default: // neutral
      return "bg-slate-100 text-slate-600";
  }
});

const dotColorClass = computed<string>(() => {
  switch (props.variant) {
    case "info":
      return "bg-app-blue-600";
    case "success":
      return "bg-app-green-600";
    case "warning":
      return "bg-app-yellow-600";
    case "danger":
      return "bg-app-red-600";
    default:
      return "bg-slate-400";
  }
});

const pillSizeClass = computed<string>(() => {
  return props.size === "sm" ? "px-px-6 py-px-2 t-xs" : "px-px-8 py-px-4 t-caption-bold";
});

const dotSizeClass = computed<string>(() => {
  return props.size === "sm" ? "h-px-6 w-px-6" : "h-px-8 w-px-8";
});
</script>

<template>
  <!-- Status dot: purely visual, aria-hidden — parent provides accessible context -->
  <span
    v-if="dot"
    :class="[dotColorClass, dotSizeClass, 'block shrink-0 rounded-full']"
    aria-hidden="true" />

  <!-- Text pill -->
  <span
    v-else
    :class="[colorClass, pillSizeClass, 'inline-flex w-fit items-center rounded-full']">
    <slot />
  </span>
</template>
